/**
 * Products section callout — Quotes / Agreements and Opportunity / More Details.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    ArrowSquareOut,
    Check,
    CheckCircle,
    Copy,
    EnvelopeSimple,
    Info,
    Phone,
    Scroll,
    Signature,
    User,
    WarningCircle,
    X,
} from '@phosphor-icons/react';
import type { OpportunitySalesPerson } from '../../@types/oneButtonType';
import type { PreContractOpportunityLink } from '../lib/preContractOpportunityLink';

export type PreContractOpportunityLinkBannerProps = {
    link: PreContractOpportunityLink;
    onReview?: () => void;
    showCommerce?: boolean;
    onOpenQuotes?: () => void;
    onOpenAgreements?: () => void;
    salesForceId?: string | null;
    opportunityNumericId?: string | number | null;
    salesPerson?: OpportunitySalesPerson | null;
    ahj?: string | null;
    onOpenDashboardV2?: () => void;
};

function salesforceOpportunityUrl(salesForceId: string): string {
    const base = String(import.meta.env.VITE_SALESFORCE_PRODUCTION_BASE_URL ?? '')
        .trim()
        .replace(/\/$/, '');
    const origin = base || 'https://trinity-solar.lightning.force.com';
    return `${origin}/lightning/r/Opportunity/${encodeURIComponent(salesForceId)}/view`;
}

function formatId(value: string | number | null | undefined): string {
    return String(value ?? '').trim();
}

async function copyText(value: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(value);
        return true;
    } catch {
        return false;
    }
}

export const PreContractOpportunityLinkBanner: React.FC<
    PreContractOpportunityLinkBannerProps
> = ({
    link,
    onReview,
    showCommerce = false,
    onOpenQuotes,
    onOpenAgreements,
    salesForceId = null,
    opportunityNumericId = null,
    salesPerson = null,
    ahj = null,
    onOpenDashboardV2,
}) => {
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    const sfid = formatId(salesForceId);
    const numericId = formatId(opportunityNumericId);
    const salesName = salesPerson?.Name?.trim() || '—';
    const salesPhone = salesPerson?.Phone?.trim() || '—';
    const salesEmail = salesPerson?.Email?.trim() || '—';
    const salesDivision = salesPerson?.Sales_Division?.trim() || '';
    const ahjLabel = String(ahj ?? '').trim() || '—';

    const showBanner =
        link.products.length > 0 || showCommerce || Boolean(sfid) || Boolean(onOpenDashboardV2);
    if (!showBanner) return null;

    const title =
        link.level === 'linked'
            ? 'Already linked to this opportunity'
            : link.level === 'partial'
              ? 'Partially linked to this opportunity'
              : link.products.length === 0
                ? 'Opportunity'
                : 'Nothing synced to this opportunity yet';

    const Icon =
        link.level === 'linked' ? CheckCircle : link.level === 'partial' ? WarningCircle : Info;

    const markCopied = useCallback((key: string) => {
        setCopiedKey(key);
        window.setTimeout(() => {
            setCopiedKey((current) => (current === key ? null : current));
        }, 1600);
    }, []);

    const handleCopy = useCallback(
        async (key: string, value: string) => {
            if (!value) return;
            const ok = await copyText(value);
            if (ok) markCopied(key);
        },
        [markCopied],
    );

    const slider = useMemo(() => {
        if (!detailsOpen) return null;
        return createPortal(
            <div className="pci-opp-details" role="dialog" aria-modal="true" aria-label="Opportunity details">
                <button
                    type="button"
                    className="pci-opp-details__backdrop"
                    aria-label="Close opportunity details"
                    onClick={() => setDetailsOpen(false)}
                />
                <aside className="pci-opp-details__slider">
                    <header className="pci-opp-details__head">
                        <p className="pci-opp-details__eyebrow">Opportunity</p>
                        <button
                            type="button"
                            className="pci-opp-details__close"
                            aria-label="Close"
                            onClick={() => setDetailsOpen(false)}
                        >
                            <X size={16} weight="bold" />
                        </button>
                    </header>
                    <div className="pci-opp-details__body">
                        <section className="pci-opp-details__card" aria-label="Identifiers">
                            <h3>IDs</h3>
                            <div className="pci-opp-details__id">
                                <div>
                                    <span className="pci-opp-details__id-label">Salesforce ID</span>
                                    <strong>{sfid || '—'}</strong>
                                </div>
                                <div className="pci-opp-details__id-actions">
                                    {sfid ? (
                                        <a
                                            href={salesforceOpportunityUrl(sfid)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="pci-opp-details__icon-btn"
                                            title="Open in Salesforce"
                                        >
                                            <ArrowSquareOut size={15} weight="bold" />
                                        </a>
                                    ) : null}
                                    <button
                                        type="button"
                                        className="pci-opp-details__icon-btn"
                                        disabled={!sfid}
                                        title="Copy Salesforce ID"
                                        onClick={() => void handleCopy('sfid', sfid)}
                                    >
                                        {copiedKey === 'sfid' ? (
                                            <Check size={15} weight="bold" />
                                        ) : (
                                            <Copy size={15} weight="bold" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="pci-opp-details__id">
                                <div>
                                    <span className="pci-opp-details__id-label">
                                        Numeric opportunity ID
                                    </span>
                                    <strong>{numericId || '—'}</strong>
                                </div>
                                <button
                                    type="button"
                                    className="pci-opp-details__icon-btn"
                                    disabled={!numericId}
                                    title="Copy numeric opportunity ID"
                                    onClick={() => void handleCopy('numeric', numericId)}
                                >
                                    {copiedKey === 'numeric' ? (
                                        <Check size={15} weight="bold" />
                                    ) : (
                                        <Copy size={15} weight="bold" />
                                    )}
                                </button>
                            </div>
                        </section>
                        <section className="pci-opp-details__card" aria-label="Salesperson">
                            <h3>Salesperson</h3>
                            {salesDivision ? (
                                <p className="pci-opp-details__division">
                                    Sales Person: {salesDivision}
                                </p>
                            ) : null}
                            <ul>
                                <li>
                                    <User size={16} weight="bold" aria-hidden />
                                    <span>{salesName}</span>
                                </li>
                                <li>
                                    <Phone size={16} weight="bold" aria-hidden />
                                    <span>{salesPhone}</span>
                                </li>
                                <li>
                                    <EnvelopeSimple size={16} weight="bold" aria-hidden />
                                    <span>{salesEmail}</span>
                                </li>
                            </ul>
                        </section>
                        <section className="pci-opp-details__card" aria-label="AHJ">
                            <h3>AHJ</h3>
                            <p>{ahjLabel}</p>
                        </section>
                    </div>
                </aside>
            </div>,
            document.body,
        );
    }, [
        ahjLabel,
        copiedKey,
        detailsOpen,
        handleCopy,
        numericId,
        salesDivision,
        salesEmail,
        salesName,
        salesPhone,
        sfid,
    ]);

    return (
        <div
            className={`pci-opp-link pci-opp-link--${link.level}`}
            role="status"
            aria-label="Opportunity link status"
        >
            <Icon className="pci-opp-link__icon" size={18} weight="fill" aria-hidden />
            <div className="pci-opp-link__body">
                <p className="pci-opp-link__title">{title}</p>
            </div>
            <div className="pci-opp-link__actions">
                {showCommerce ? (
                    <>
                        <button
                            type="button"
                            className="pci-opp-link__chip pci-opp-link__chip--action"
                            onClick={onOpenQuotes}
                        >
                            <Scroll size={12} weight="fill" aria-hidden />
                            Quotes
                        </button>
                        <button
                            type="button"
                            className="pci-opp-link__chip pci-opp-link__chip--action"
                            onClick={onOpenAgreements}
                        >
                            <Signature size={12} weight="fill" aria-hidden />
                            Agreements
                        </button>
                    </>
                ) : null}
                {sfid || onOpenDashboardV2 ? (
                    <div className="pci-opp-link__opp-group">
                        <button
                            type="button"
                            className="pci-opp-link__opp-btn"
                            onClick={() => setDetailsOpen(true)}
                            title={sfid || 'Opportunity Info'}
                        >
                            Opportunity Info
                        </button>
                        {onOpenDashboardV2 ? (
                            <button
                                type="button"
                                className="pci-opp-link__more-btn"
                                onClick={onOpenDashboardV2}
                            >
                                Dashboard
                            </button>
                        ) : null}
                    </div>
                ) : null}
                {onReview ? (
                    <button type="button" className="pci-opp-link__action" onClick={onReview}>
                        Review products
                    </button>
                ) : null}
            </div>
            {slider}
        </div>
    );
};

export default PreContractOpportunityLinkBanner;
